import SparkMD5 from 'spark-md5'

/**
 * 计算文件的 MD5 值
 * @param file - 需要计算 MD5 的文件 (File 类型)
 * @returns Promise<string> - 返回文件的 MD5 值
 */
export async function getFileMd5(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const chunkSize = 2 * 1024 * 1024 // 每块大小 2MB
    const chunks = Math.ceil(file.size / chunkSize) // 总块数
    let currentChunk = 0 // 当前块索引
    const spark = new SparkMD5.ArrayBuffer() // 创建 MD5 计算器
    const fileReader = new FileReader() // 文件读取器

    // 文件块读取完成后触发
    fileReader.onload = e => {
      if (e.target?.result) {
        spark.append(e.target.result as ArrayBuffer) // 添加块内容
        currentChunk++

        currentChunk < chunks ? loadNext() : resolve(spark.end()) // 继续或完成
      } else {
        reject(new Error('文件块读取失败'))
      }
    }

    // 文件读取出错
    fileReader.onerror = () => reject(new Error('文件读取出错'))

    // 读取下一个文件块
    function loadNext() {
      const start = currentChunk * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      fileReader.readAsArrayBuffer(file.slice(start, end))
    }

    loadNext() // 开始读取第一个块
  })
}

/**
 * 将字节数格式化为易读的字符串形式，例如 "1024 B" 或 "1.00 MB"。
 *
 * @param bytes - 字节数
 * @returns 格式化后的字符串，带合适的单位（B, KB, MB, GB, TB, PB）
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B' // 特殊情况：字节数为 0

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const k = 1024 // 1 KB = 1024 B
  const i = Math.floor(Math.log(bytes) / Math.log(k)) // 确定单位
  const size = bytes / Math.pow(k, i) // 计算转换后的大小

  return `${size.toFixed(2)} ${units[i]}` // 保留两位小数
}

/**
 * 格式化字节速率为字符串形式，例如 "1024 B/s" 或 "1.00 MB/s"。
 *
 * @param bytes - 每秒的字节数
 * @returns 格式化后的速率字符串，单位为 B/s, KB/s, MB/s 等
 */
export function formatRateBytes(bytes: number): string {
  return formatBytes(bytes) + '/s'
}
